<template>
    <div class="global-search" data-manual-search>
        <label class="global-search__scope">
            <span>Cerca in</span>
            <select v-model="scope">
                <option v-for="option in scopeOptions" :key="option.value" :value="option.value" :disabled="option.disabled">{{ option.text }}</option>
            </select>
        </label>
        <div class="global-search__input">
                    <v-combobox ref="searchbox" v-model="searchModel" :items="searchItems([{ hits: indexHits }])" item-text="codice" return-object no-filter
                        :search-input.sync="searchKeyword" @focus="prepareSearch" @keydown.enter.prevent="goToRoute"
                        clearable hide-details dense outlined color="white" :label="$route.meta.menu === 'acquisti' ? 'Ricerca globale' : 'Cerca'" :aria-label="$route.meta.menu === 'acquisti' ? 'Ricerca globale' : 'Cerca'" :placeholder="$route.meta.menu === 'acquisti' ? 'Cerca ovunque: ordine, articolo…' : hint" :loading="searchLoading || ddtSearchLoading || nestingLoading || salesLoading"
                        :hide-no-data="!searchKeyword" :no-data-text="noResultsText">
                        <template v-if="hasPastedPath" #no-data>
                            <v-list-item><v-list-item-title role="status">Non trovato</v-list-item-title></v-list-item>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <v-list-item-title><span v-html="highlight(item.codice, searchKeyword)" /></v-list-item-title>
                                <v-list-item-subtitle v-if="customerArticleCodes(item).length" class="global-search__customer-code">
                                    Codice articolo cliente: {{ customerArticleCodes(item).join(', ') }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle>{{ item.descrizione }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action><v-chip x-small outlined>{{ resultTypeLabel(item) }}</v-chip></v-list-item-action>
                        </template>
                        <template #append-item>
                            <v-list-item v-if="searchKeyword && !hasPastedPath" @click="goToRoute"><v-list-item-title>Mostra tutti i risultati</v-list-item-title><v-icon>mdi-arrow-right</v-icon></v-list-item>
                            <v-list-item v-if="sourceError" dense><v-list-item-subtitle role="status">{{ sourceError }}</v-list-item-subtitle></v-list-item>
                            <v-list-item v-if="usesPartialSearch" dense><v-list-item-content><span class="caption" role="status">{{ searchPartialMessage }}</span></v-list-item-content></v-list-item>
                        </template>
                    </v-combobox>
        </div>
    </div>
</template>
<script>
import { defineComponent } from 'vue';
import { createLazySearchClient } from '@/utils/lazySearchClient.mjs';
import { emptySearchResponse } from '@/utils/typesenseSearchClient';
import { rankSearchHits, matchingCustomerArticleCodes } from '@/utils/searchResultRanking.mjs';
import { createArticleFirstSearchClient } from '@/utils/articleFirstSearchClient.mjs';
import { createCodeSearchClient, resolveDashboardCode, partialCodeSearchMessage, isPastedFilePath } from '@/utils/codeSearchClient.mjs';
import _ from 'lodash';
import { nestingSearchHit } from '@/components/laser/nestingPage.mjs';
import ddtSearchMixin from './ddtSearchMixin.mjs';
import salesOrderSearchMixin from '../search/salesOrderSearchMixin.mjs';
import { SEARCH_SCOPES, usesIndex, scopeFacetFilters, searchRoute, searchHint, resultTypeLabel } from '../search/searchScopes.mjs';
async function loadIndexClient() {
    const { default: TypesenseInstantSearchAdapter } = await import(/* webpackChunkName: "header-search-engine" */ 'typesense-instantsearch-adapter');
    return new TypesenseInstantSearchAdapter({
    // test ricerca https://bfritscher.github.io/typesense-dashboard/#/login
    server: {
        apiKey: "2JD3dhRxUfSzRVGJHp1v25xI1KVHV2FN", // Be sure to use an API key that only allows search operations
        nodes: [
            {
                host: "ts.mecomsrl.com",
                path: "", // Optional. Example: If you have your typesense mounted in localhost:8108/typesense, path should be equal to '/typesense'
                port: "443", // window.location.protocol === "https:" ? "443" : "80",
                protocol: "https", //window.location.protocol === "https:" ? "https" : "http",
            },
        ],
        cacheSearchResultsForSeconds: 2 * 60, // Cache search results from server. Defaults to 2 minutes. Set to 0 to disable caching.
    },
    // The following parameters are directly passed to Typesense's search API endpoint.
    //  So you can pass any parameters supported by the search endpoint below.
    //  query_by is required.
    additionalSearchParameters: {
        query_by:
            "codice_tokenized,articolo,descrizione,articolo_cli_tokenized,articolo_cli,cliente_facet,ragione_sociale_facet,varie,nestings,disegno_content",
        sort_by: "_text_match(buckets: 10):desc,orderby:asc",
    },
    }).searchClient;
}
export default defineComponent({
    name: 'SearchBox',
    mixins: [ddtSearchMixin, salesOrderSearchMixin],
    data() {
        const lazyClient = createLazySearchClient(loadIndexClient);
        const codeClient = createCodeSearchClient(lazyClient, query => resolveDashboardCode(this.$work, query));
        const client = createArticleFirstSearchClient(codeClient, () => this.scope);
        return {
            searchClient: { ...client, search: requests => usesIndex(this.scope) && requests.some(request => String(request.params?.query || '').trim()) ? client.search(requests) : Promise.resolve(emptySearchResponse(requests)) },
            prepareIndexClient: lazyClient.prepare, indexHits: [], indexRequestId: 0,
            scope: 'all', searchKeyword: '', searchModel: null, indexQuery: '', searchLoading: false, searchError: false, searchErrorMessage: '',
            searchPartialMessage: '',
            nestingItems: [], nestingLoading: false, nestingError: '', nestingRequestId: 0,
            searchTimer: null, nestingTimer: null, navigating: false,
        };
    },
    computed: {
        hasPastedPath() { return isPastedFilePath(this.searchKeyword); },
        usesPartialSearch() { return !this.hasPastedPath && usesIndex(this.scope) && !this.searchError && this.indexQuery === String(this.searchKeyword || '').trim() && Boolean(this.searchPartialMessage); },
        facetFilters() { return scopeFacetFilters(this.scope); },
        hint() { return searchHint(this.scope); },
        scopeOptions() { return SEARCH_SCOPES.map(option => ({ ...option, disabled: option.value === 'ddt' && !this.canSearchDdt || option.value === 'sales' && !this.canSearchSales })); },
        sourceError() { return this.hasPastedPath ? '' : this.ddtSearchError || this.salesError || this.nestingError || (usesIndex(this.scope) && this.searchError ? this.searchErrorMessage : ''); },
        noResultsText() { return this.hasPastedPath ? 'Non trovato' : this.sourceError || (this.searchLoading || this.ddtSearchLoading || this.nestingLoading || this.salesLoading ? 'Ricerca in corso…' : 'Nessun risultato. Premi Invio per aprire la ricerca.'); },
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer); clearTimeout(this.nestingTimer); this.nestingRequestId++; this.indexRequestId++;
    },
    watch: {
        searchKeyword() { this.scheduleSources(); this.startSearch(); },
        scope() { this.indexQuery = ''; this.searchError = false; this.searchLoading = false; this.scheduleSources(); this.startSearch(); },
        '$store.getters.token'() { this.reset(); this.startSearch(); },
        searchModel(value) { if (value && typeof value === 'object' && value.codice) this.openRoute(this.routeFromHit(value)); },
    },
    methods: {
        reset() { this.searchKeyword = ''; this.searchModel = null; this.scheduleSources(); },
        resultTypeLabel,
        customerArticleCodes(hit) { return matchingCustomerArticleCodes(hit, this.searchKeyword); },
        searchItems(indices) {
            const term = String(this.searchKeyword || '').trim();
            if (!term || this.hasPastedPath) return [];
            const indexed = usesIndex(this.scope) && !this.searchError && this.indexQuery === term ? rankSearchHits(indices?.[0]?.hits || [], term) : [];
            const hits = [...this.ddtHits, ...this.salesHits, ...this.nestingItems, ...indexed].filter((hit, index, hits) => hits.findIndex(other => other.route === hit.route && other.codice === hit.codice) === index);
            return rankSearchHits(hits, term);
        },
        highlight(text, query) {
            const escaped = _.escape(String(text || ''));
            const tokens = [...new Set(String(query || '').split(/\s+/).filter(token => token.length > 1).map(token => _.escapeRegExp(_.escape(token))))];
            return tokens.length ? escaped.replace(new RegExp(tokens.join('|'), 'gi'), match => `<span class="highlight">${match}</span>`) : escaped;
        },
        prepareSearch() {
            // A failed optional warm-up is retried when text is entered.
            if (usesIndex(this.scope)) this.prepareIndexClient().catch(() => {});
        },
        async loadIndex(term, requestId) {
            const scope = this.scope;
            const identity = this.$store?.getters?.token;
            const current = () => requestId === this.indexRequestId && !this._isDestroyed &&
                scope === this.scope && identity === this.$store?.getters?.token &&
                term === String(this.searchKeyword || '').trim();
            try {
                const result = await this.searchClient.search([{ indexName: 'work-search', params: { query: term, hitsPerPage: 20, page: 0, facetFilters: this.facetFilters } }]);
                if (!current()) return;
                if (!Array.isArray(result?.results?.[0]?.hits)) throw new Error('Risposta ricerca non valida');
                this.indexHits = result.results[0].hits;
                this.indexQuery = term;
                this.searchPartialMessage = partialCodeSearchMessage(result);
            } catch (error) {
                if (!current()) return;
                this.searchError = true;
                this.searchErrorMessage = error.searchMessage || 'Ricerca articoli e ODP non disponibile. Apri la ricerca per riprovare.';
            } finally {
                if (current()) this.searchLoading = false;
            }
        },
        startSearch(refine) {
            clearTimeout(this.searchTimer);
            const requestId = ++this.indexRequestId;
            this.indexHits = []; this.indexQuery = ''; this.searchError = false; this.searchPartialMessage = '';
            const run = typeof refine === 'function' ? refine : term => this.loadIndex(term, requestId);
            if (this.hasPastedPath) { this.searchLoading = false; if (typeof refine === 'function') refine(''); return; }
            const term = String(this.searchKeyword || '').trim();
            this.searchLoading = Boolean(term && usesIndex(this.scope));
            if (this.searchLoading) this.searchTimer = setTimeout(() => run(term), 200);
        },
        scheduleSources() {
            clearTimeout(this.nestingTimer); ++this.nestingRequestId; this.nestingItems = []; this.nestingError = ''; this.nestingLoading = false;
            const term = this.hasPastedPath ? '' : String(this.searchKeyword || '').trim();
            this.scheduleDdtSearch(['all', 'ddt'].includes(this.scope) ? term : '');
            this.scheduleSalesSearch(['all', 'sales'].includes(this.scope) ? term : '', true);
            if (['all', 'nesting'].includes(this.scope) && term.length >= 2) {
                this.nestingLoading = true;
                this.nestingTimer = setTimeout(() => this.loadNestingItems(term), 200);
            }
        },
        async loadNestingItems(query) {
            const id = ++this.nestingRequestId;
            try {
                const response = await this.$work.get('/oseon/v2/nestings/search/', { q: query, limit: 6 }, false, null, { ignoreGlobalErrorStatuses: [400, 403, 500, 503] });
                if (!Array.isArray(response?.data?.results)) throw new Error('Risposta non valida');
                if (id === this.nestingRequestId) this.nestingItems = response.data.results.map(nestingSearchHit);
            } catch (error) { if (id === this.nestingRequestId) this.nestingError = 'Ricerca nesting non disponibile. Apri la ricerca per riprovare.'; }
            finally { if (id === this.nestingRequestId) this.nestingLoading = false; }
        },
        routeFromHit(hit) {
            if (hit.route === 'DdtDettaglio') return { name: hit.route, params: { numero: hit.codice } };
            if (hit.route === 'av-operatore') return { name: 'Ordine', params: { codice: String(hit.ordine || hit.codice).split('/')[0] } };
            return { name: hit.route || 'Search', params: { codice: hit.codice } };
        },
        openRoute(route) {
            if (this.navigating) return;
            this.navigating = true;
            this.$refs.searchbox?.blur();
            this.$router.push(route).catch(() => {}).finally(() => { this.navigating = false; this.searchModel = null; this.searchKeyword = ''; });
        },
        goToRoute() { if (!this.hasPastedPath) this.openRoute(searchRoute(String(this.searchKeyword || ''), this.scope)); },
    },
});
</script>
<style scoped>
.global-search { display: flex; align-items: center; gap: 8px; width: 45%; min-width: 300px; }
.global-search__scope { flex: 0 0 135px; position: relative; }
.global-search__scope > span { position: absolute; top: var(--space-1); left: var(--space-2); font-size: 12px; line-height: 1; pointer-events: none; }
/* appearance: none: il select nativo di Safari/macOS ignora padding e altezza e sovrappone l'etichetta; 40px = altezza del campo Cerca. */
.global-search__scope select { width: 100%; height: 40px; min-height: 40px; padding: 15px 26px 0 var(--space-2); border: var(--kpi-line) solid currentColor; border-radius: var(--radius-sm); color: inherit; background: transparent; font: inherit; line-height: 1.2; text-overflow: ellipsis; -webkit-appearance: none; appearance: none; }
.global-search__scope::after { content: ""; position: absolute; right: var(--space-3); top: 50%; width: 6px; height: 6px; border-right: 2px solid currentColor; border-bottom: 2px solid currentColor; transform: translateY(-75%) rotate(45deg); pointer-events: none; }
.global-search__scope option { color: var(--ink); background: var(--surface); }
.global-search__input { flex: 1; min-width: 0; }
.global-search__customer-code { white-space: normal; overflow-wrap: anywhere; }
::v-deep .highlight { background-color: #ffeb3b; color: #212121; }
</style>
